import { z } from "zod";
import { timestampToDate } from "@/core/firebase/converters";
import { PaymentMethodSchema } from "@/core/models/booking";

/**
 * Player credit — the wallet ledger (spec/clubmanager/18-credit-monedero).
 *
 * A player's balance is NEVER stored as an editable scalar. It is the
 * `sum(walletEntries)` per scope; `crmPlayers/{id}.walletSummary` is a derived,
 * CF-maintained cache, reconstructible from the ledger (BR-3). Entries are
 * append-only — no edits, no deletes; corrections are new `adjustment` entries
 * (BR-7). Spend/refund/expiry entries are written ONLY by Cloud Functions (BR-8).
 *
 * Stored at `clubs/{clubId}/walletEntries/{id}`. Three scopes (BR-4): `cash`
 * (EUR, never expires), `bono` (session count, expires per pack), `membership`
 * (hours, expires per cycle — modelled but dormant until the memberships module
 * #107 produces `membership_grant`). See `apps/clubmanager-pwa/docs/wallet.md`.
 */

/** Which sub-balance an entry belongs to. Unit follows from scope (BR-4). */
export const WALLET_SCOPE = ["cash", "bono", "membership"] as const;
export const WalletScopeSchema = z.enum(WALLET_SCOPE);
export type WalletScope = z.infer<typeof WalletScopeSchema>;

/** Amount unit — disambiguates `amount`; EUR for cash, counts for bono/membership. */
export const WALLET_UNIT = ["EUR", "sessions", "hours"] as const;
export const WalletUnitSchema = z.enum(WALLET_UNIT);
export type WalletUnit = z.infer<typeof WalletUnitSchema>;

/**
 * Ledger entry type. `+` credits the player, `−` debits. Client-writable:
 * `topup`, `bono_grant`, `adjustment` (see rules). CF-only: `topup_bonus`,
 * `refund_credit`, `booking_spend`, `bono_spend`, `bono_refund`, `expiry`,
 * `membership_grant`.
 */
export const WALLET_ENTRY_TYPE = [
  "topup",
  "topup_bonus",
  "refund_credit",
  "booking_spend",
  "adjustment",
  "expiry",
  "bono_grant",
  "bono_spend",
  "bono_refund",
  "membership_grant",
] as const;
export const WalletEntryTypeSchema = z.enum(WALLET_ENTRY_TYPE);
export type WalletEntryType = z.infer<typeof WalletEntryTypeSchema>;

/** What a bono pack (and its grant) may be redeemed for (BR-11 / BR-12). */
export const WALLET_REDEEMABLE_FOR = ["court", "class", "any"] as const;
export const WalletRedeemableForSchema = z.enum(WALLET_REDEEMABLE_FOR);
export type WalletRedeemableFor = z.infer<typeof WalletRedeemableForSchema>;

export const WalletEntrySchema = z.object({
  /** Doc id. */
  id: z.string(),
  clubId: z.string(),
  /** Owner-locked to a CrmPlayer (BR-5). */
  playerId: z.string(),
  scope: WalletScopeSchema,
  type: WalletEntryTypeSchema,
  /** Signed: + credit, − debit. EUR (cash) or count (bono/membership). */
  amount: z.number(),
  unit: WalletUnitSchema,
  /** Free text; required (non-empty) for `adjustment` (BR-10). */
  reason: z.string().default(""),
  /** Tender for a `topup` (BR-9). Reuses the booking payment-method enum so
   *  cash-drawer reconciliation can query top-ups by method; null otherwise. */
  method: PaymentMethodSchema.nullable().optional(),
  /** Set on `booking_spend` / `refund_credit`. */
  bookingId: z.string().nullable().optional(),
  /** Links a `bono_spend` to its granting `bono_grant` entry id. */
  bonoId: z.string().nullable().optional(),
  /** Set on `bono_grant`: which pack product this grant came from. */
  bonoProductId: z.string().nullable().optional(),
  /** Set on `bono_grant`: what the grant redeems for (BR-12). */
  redeemableFor: WalletRedeemableForSchema.nullable().optional(),
  /** Set on `bono_grant`: denormalized pack name for the ledger + summary. */
  productName: z.string().nullable().optional(),
  /** Set on `bono_grant`: € value of one session (packPrice / sessionCount).
   *  Released to earned revenue when a session is consumed — revenue at
   *  consumption on the class day (BR-12a, bono scope). */
  sessionValue: z.number().nullable().optional(),
  /** Set on `bono_grant`: the pack sale price in EUR (cash collected at sale).
   *  Feeds the "Cobrado/Caja" number + cash-drawer reconciliation. The €-in at
   *  sale is a deferred-revenue liability, not revenue (BR-11 / BR-9a). */
  packPrice: z.number().nullable().optional(),
  /** Set on `membership_grant` (dormant until #107). */
  membershipCycleId: z.string().nullable().optional(),
  /** Set on `bono_grant` / `membership_grant`; cash never expires (BR-17). */
  expiresAt: timestampToDate.nullable().optional(),
  createdAt: timestampToDate,
  createdByUid: z.string(),
  createdByName: z.string(),
});
export type WalletEntry = z.infer<typeof WalletEntrySchema>;

/** One live bono grant's residual state, computed for the summary. */
export const BonoBalanceSchema = z.object({
  /** The granting entry id. */
  bonoId: z.string(),
  productName: z.string(),
  /** Sessions left = grant amount + Σ(bono_spend for this bonoId). ≥ 0. */
  remaining: z.number().int(),
  /** € value of one session — applied to a booking when a session is used. */
  sessionValue: z.number().default(0),
  redeemableFor: WalletRedeemableForSchema,
  expiresAt: timestampToDate.nullable().optional(),
});
export type BonoBalance = z.infer<typeof BonoBalanceSchema>;

/**
 * Derived read cache on `crmPlayers/{id}.walletSummary`, rebuilt by
 * `on_wallet_entry_written` and reconstructible via `recompute_wallet_summary`.
 */
export const PlayerWalletSummarySchema = z.object({
  /** EUR; Σ cash-scope entries. Never negative (BR-6). */
  cashBalance: z.number().default(0),
  /** Per-grant, expired grants excluded. */
  bonoBalances: z.array(BonoBalanceSchema).default([]),
  /** Hours; 0 until #107. */
  membershipBalance: z.number().default(0),
  /** Earliest expiry across bono/membership grants — reception warning. */
  nextExpiryAt: timestampToDate.nullable().optional(),
  lastActivityAt: timestampToDate.nullable().optional(),
});
export type PlayerWalletSummary = z.infer<typeof PlayerWalletSummarySchema>;

export const EMPTY_WALLET_SUMMARY: PlayerWalletSummary = {
  cashBalance: 0,
  bonoBalances: [],
  membershipBalance: 0,
};

/**
 * Club-wide wallet liability rollup at `clubs/{clubId}/aggregates/walletLiability`,
 * maintained incrementally by `on_wallet_entry_written` (`FieldValue.increment`
 * per entry) + the expiry CF, rebuildable via `recompute_club_wallet_aggregate`.
 * CF-only write. Feeds the owner numbers (BR-12b / BR-18): *Saldo en circulación*
 * (`cashFloat`) and *Bonos pendientes* (`bonoLiability`). The period *Cobrado* +
 * *Devengado* numbers come from the ledger / payments projection, not here.
 */
export const ClubWalletAggregateSchema = z.object({
  /** Σ all players' cash balances — the club's prepaid-cash liability (BR-18). */
  cashFloat: z.number().default(0),
  /** Σ (remaining × sessionValue) across live bono grants — bono liability (EUR). */
  bonoLiability: z.number().default(0),
  /** Σ remaining sessions across all live grants. */
  bonoSessions: z.number().default(0),
  /** Cumulative value forfeited at expiry → recognized as breakage revenue. */
  breakageRevenue: z.number().default(0),
  updatedAt: timestampToDate.nullable().optional(),
});
export type ClubWalletAggregate = z.infer<typeof ClubWalletAggregateSchema>;

export const EMPTY_CLUB_WALLET_AGGREGATE: ClubWalletAggregate = {
  cashFloat: 0,
  bonoLiability: 0,
  bonoSessions: 0,
  breakageRevenue: 0,
};

/**
 * Club-defined bono pack template, stored at `clubs/{clubId}/bono_products/{id}`.
 * Selling one creates a `bono_grant` entry with `remaining = sessionCount`,
 * `expiresAt = now + expiryDays`, and the `redeemableFor` tag (BR-11). Kept
 * separate from `catalog_products` — bonos carry session + redeemability
 * semantics the sales catalogue does not model.
 */
export const BonoProductSchema = z.object({
  id: z.string(),
  clubId: z.string(),
  name: z.string(),
  /** How many sessions the pack grants. */
  sessionCount: z.number().int().positive(),
  redeemableFor: WalletRedeemableForSchema,
  /** Sale price in EUR. */
  price: z.number().nonnegative(),
  /** Grant validity window; default 365 days (BR-11). */
  expiryDays: z.number().int().positive().default(365),
  active: z.boolean().default(true),
  createdAt: timestampToDate,
  createdByUid: z.string(),
});
export type BonoProduct = z.infer<typeof BonoProductSchema>;

/** Editable fields of a bono product — the create/edit form input. The
 *  repository stamps `id` / `clubId` / `createdByUid` / `createdAt`. */
export const BonoProductInputSchema = BonoProductSchema.omit({
  id: true,
  clubId: true,
  createdAt: true,
  createdByUid: true,
});
export type BonoProductInput = z.infer<typeof BonoProductInputSchema>;

/**
 * Wallet sub-config, nested on the club document as `walletPolicy`. This is
 * NOT the on/off switch — activation is the owner opt-in `features.walletEnabled`
 * (self-serve GA). Only the tuning knobs live here.
 */
export const TopupBonusBracketSchema = z.object({
  /** Minimum top-up (EUR) to earn the bonus. */
  minTopup: z.number().positive(),
  /** Bonus percentage added as a `topup_bonus` entry (v1.1). */
  bonusPercent: z.number().nonnegative(),
});
export type TopupBonusBracket = z.infer<typeof TopupBonusBracketSchema>;

/**
 * What happens to a bono grant's remaining sessions when it expires (BR-17).
 * `forfeit` — remaining liability is released to breakage revenue (default).
 * `refund` — remaining value returns to the player's cash balance.
 * Operational policy, not accounting policy.
 */
export const BONO_EXPIRY_POLICY = ["forfeit", "refund"] as const;
export const BonoExpiryPolicySchema = z.enum(BONO_EXPIRY_POLICY);
export type BonoExpiryPolicy = z.infer<typeof BonoExpiryPolicySchema>;

export const WalletPolicySchema = z.object({
  /** Default expiry applied to new bono grants unless the product overrides. */
  bonoDefaultExpiryDays: z.number().int().positive().default(365),
  /** Default `redeemableFor` pre-filled when creating a new bono product. */
  bonoDefaultScope: WalletRedeemableForSchema.default("class"),
  /** What expiry does with a grant's remaining sessions (BR-17). */
  bonoExpiryPolicy: BonoExpiryPolicySchema.default("forfeit"),
  /** Optional promo brackets (v1.1); empty = no top-up bonus. */
  topupBonusBrackets: z.array(TopupBonusBracketSchema).default([]),
});
export type WalletPolicy = z.infer<typeof WalletPolicySchema>;

export const DEFAULT_WALLET_POLICY: WalletPolicy = {
  bonoDefaultExpiryDays: 365,
  bonoDefaultScope: "class",
  bonoExpiryPolicy: "forfeit",
  topupBonusBrackets: [],
};
